import React, { useMemo, useState } from 'react';
import { X, Search, Plus } from 'lucide-react';
import { WIDGETS, CATEGORIES, allowed } from './registry';

export default function WidgetLibrary({ open, onClose, onAdd, user, boardName }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const list = useMemo(() => Object.entries(WIDGETS)
    .filter(([, m]) => allowed(m, user))
    .filter(([, m]) => cat === 'all' || m.cat === cat)
    .filter(([, m]) => !q || `${m.title} ${m.desc}`.toLowerCase().includes(q.toLowerCase())), [q, cat, user]);
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[80] bg-slate-900/30 backdrop-blur-[2px]" onClick={onClose} data-testid="library-backdrop" />
      <aside className="fixed right-0 top-0 bottom-0 z-[90] w-full sm:w-[420px] bg-white shadow-2xl flex flex-col animate-slide-in" data-testid="widget-library">
        <div className="px-5 pt-5 pb-3 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-black text-slate-900">Add a widget</h2>
              <p className="text-xs text-slate-500">Goes onto <span className="font-semibold text-slate-700">{boardName}</span>. Drag it anywhere afterwards.</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="library-close"><X className="h-5 w-5" /></button>
          </div>
          <div className="relative mt-3">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search widgets…" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="library-search" />
          </div>
          <div className="flex gap-1 mt-3 flex-wrap">
            {[['all', 'All'], ...CATEGORIES].map(([k, l]) => (
              <button key={k} onClick={() => setCat(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cat === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`library-cat-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {list.map(([type, m]) => (
            <div key={type} className="group flex items-start gap-3 rounded-xl border border-slate-200 p-3 hover:border-blue-300 hover:bg-blue-50/40 transition-colors" data-testid={`library-item-${type}`}>
              <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0"><m.icon className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><p className="text-sm font-bold text-slate-900">{m.title}</p><span className="text-[10px] uppercase tracking-wide text-slate-400">{CATEGORIES.find(c => c[0] === m.cat)?.[1]}</span></div>
                <p className="text-xs text-slate-600 mt-0.5 leading-snug">{m.desc}</p>
              </div>
              <button onClick={() => onAdd(type)} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold" data-testid={`library-add-${type}`}><Plus className="h-3.5 w-3.5" />Add</button>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-slate-400 p-4 text-center">Nothing matches.</p>}
        </div>
      </aside>
    </>
  );
}
